import { useCallback } from "react"

import { useTestSetsList } from "@/test-set/catalog"
import {
  mapAddTestSetRowsInputToTransport,
  mapCreateTestSetInputToTransport,
  useAddTestSetRowsTransport,
  useCreateTestSetTransport,
  useInvalidateTestSetTransportQueries,
} from "@/test-set/transport"
import { useTransportQueryUtils } from "@/transport"

import type { TestCaseInput } from "@/types/TestCaseInput"
import { TestSetType } from "@/types/TestSetInput"

/**
 * Route-owned transport boundary for the uploaded conversation bulk action.
 * Exposes the list of test sets and the callbacks the component needs, so the
 * component itself stays clear of `@/transport` and `@/test-set/*` imports.
 */
export function useAddToTestSetTransport() {
  const { data: testSets } = useTestSetsList()
  const { mutateAsync: addTestSetRows } = useAddTestSetRowsTransport()
  const { mutateAsync: createTestSet } = useCreateTestSetTransport()
  const invalidateTestSetTransportQueries =
    useInvalidateTestSetTransportQueries()
  const transportQueryUtils = useTransportQueryUtils()

  const fetchTranscript = useCallback(
    async (simulationOutputId: string) => {
      const response = await transportQueryUtils.fetch("getSimulationOutput", {
        simulation_output_id: simulationOutputId,
      })
      return response.simulation_output?.transcript ?? null
    },
    [transportQueryUtils],
  )

  const addToExistingTestSet = useCallback(
    async (testSetId: string, inputs: TestCaseInput[]) => {
      await addTestSetRows(
        mapAddTestSetRowsInputToTransport({ testSetId, inputs }),
      )
      // Defer so the toast/modal can paint before React Query invalidations.
      setTimeout(() => {
        void invalidateTestSetTransportQueries()
      }, 0)
    },
    [addTestSetRows, invalidateTestSetTransportQueries],
  )

  const createTestSetWithRows = useCallback(
    async (params: {
      displayName: string
      description: string
      inputs: TestCaseInput[]
    }) => {
      await createTestSet(
        mapCreateTestSetInputToTransport({
          testSetInput: {
            display_name: params.displayName,
            description: params.description,
            test_set_type: TestSetType.TRANSCRIPT,
            testCases: params.inputs,
            test_set_metadata: {},
          },
        }),
      )
      setTimeout(() => {
        void invalidateTestSetTransportQueries()
      }, 0)
    },
    [createTestSet, invalidateTestSetTransportQueries],
  )

  return {
    testSets,
    fetchTranscript,
    addToExistingTestSet,
    createTestSetWithRows,
  }
}
